import { styled } from "@mui/material/styles";
import { Box, Typography, CircularProgress } from "@mui/material";

export const LocationTreeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: "30px",
  minHeight: "320px",
  maxHeight: "320px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[2],
    transform: "translateY(-1px)",
  },
}));

export const LocationTreeHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

export const LocationTreeTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textAlign: "center",
}));

export const LocationTreeContent = styled(Box)(({ theme }) => ({
  flex: 1,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  overflowY: "auto",
  overflowX: "hidden",
  padding: theme.spacing(1),
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.grey[100],
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[400],
    borderRadius: "3px",
    "&:hover": {
      backgroundColor: theme.palette.grey[500],
    },
  },
}));

export const StyledTreeView = styled(Box)(({ theme }) => ({
  "& .MuiTreeView-root": {
    flexGrow: 1,
  },
  "& .MuiTreeItem-root": {
    "&:focus > .MuiTreeItem-content .MuiTreeItem-label": {
      backgroundColor: "transparent !important",
    },
  },
  "& .MuiTreeItem-content": {
    padding: theme.spacing(0.5, 1),
    borderRadius: "4px",
    margin: theme.spacing(0.25, 0),
    transition: "background-color 0.2s ease-in-out",
    minHeight: "36px",
    backgroundColor: "transparent !important",
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    "&.Mui-selected": {
      backgroundColor: "transparent !important",
      "&:hover": {
        backgroundColor: "transparent !important",
      },
    },
    "&.Mui-focused": {
      backgroundColor: "transparent !important",
    },
  },
  "& .MuiTreeItem-label": {
    fontSize: "0.875rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: 0,
  },
  "& .MuiTreeItem-group": {
    marginLeft: theme.spacing(2.5),
    paddingLeft: theme.spacing(0.5),
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "100%": {
      opacity: 0.4,
    },
  },
}));

export const LocationTreeNodeLabel = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  gap: theme.spacing(1),
  minHeight: "28px",
  position: "relative",
}));

export const LocationNodeName = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: "0 0 auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  maxWidth: "200px",
}));

export const LocationNodeDashLine = styled(Box)(({ theme }) => ({
  flex: 1,
  height: "1px",
  backgroundColor: theme.palette.divider,
  margin: `0 ${theme.spacing(1)}`,
  position: "relative",
  "&::before": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `repeating-linear-gradient(
      to right,
      ${theme.palette.divider} 0px,
      ${theme.palette.divider} 4px,
      transparent 4px,
      transparent 8px
    )`,
  },
}));

export const LocationNodeValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  minWidth: "fit-content",
  textAlign: "right",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
}));

export const LocationNodeIcon = styled(Box)(({ theme }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  marginRight: theme.spacing(0.5),
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
}));

export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "180px",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const LoadingText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  textAlign: "center",
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "200px",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  textAlign: "center",
}));

export const NodeLoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  width: "16px !important",
  height: "16px !important",
  color: theme.palette.text.secondary,
}));

export const TreeViewActions = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const FallbackContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "180px",
  width: "100%",
}));

export const FallbackIcon = styled("i")(({ theme }) => ({
  fontSize: "48px",
  color: theme.palette.neutral.darkGreyish,
}));

export const SkeletonContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(0.5, 1),
}));

export const SkeletonBar = styled(Box)(({ theme }) => ({
  width: "90%",
  height: 15,
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.spacing(0.5),
  animation: "pulse 1.5s ease-in-out infinite alternate",
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "100%": {
      opacity: 0.4,
    },
  },
}));
